import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { UserCircle, Plus, Edit, Trash2, Store } from "lucide-react";

const StoreManagerSettings = ({ restaurants, user }) => {
  const [storeManagers, setStoreManagers] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ isOpen: false, manager: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, managerId: null });
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    restaurant_id: "",
    phone: "",
    email: "",
  });

  const fetchStoreManagers = async () => {
    try {
      const response = await axiosInstance.get("/store-managers");
      setStoreManagers(response.data);
    } catch (error) {
      toast.error("Failed to fetch store managers");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchStoreManagers();
  }, []);

  const handleAdd = async () => {
    if (!formData.name || !formData.restaurant_id) {
      toast.error("Please fill in name and select a restaurant");
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.post("/store-managers", formData);
      toast.success("Store manager added successfully!");
      setAddDialog(false);
      resetForm();
      fetchStoreManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add store manager");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.manager || !formData.name || !formData.restaurant_id) {
      toast.error("Please fill in name and select a restaurant");
      return;
    }

    setLoading(true);
    try {
      await axiosInstance.put(`/store-managers/${editDialog.manager.id}`, formData);
      toast.success("Store manager updated successfully!");
      setEditDialog({ isOpen: false, manager: null });
      resetForm();
      fetchStoreManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update store manager");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/store-managers/${deleteDialog.managerId}`);
      toast.success("Store manager deleted successfully!");
      setDeleteDialog({ isOpen: false, managerId: null });
      fetchStoreManagers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete store manager");
    }
  };

  const handleEdit = (manager) => {
    setFormData({
      name: manager.name,
      restaurant_id: manager.restaurant_id,
      phone: manager.phone || "",
      email: manager.email || "",
    });
    setEditDialog({ isOpen: true, manager });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      restaurant_id: "",
      phone: "",
      email: "",
    });
  };

  // Group managers by restaurant
  const managersByRestaurant = storeManagers.reduce((acc, manager) => {
    const restId = manager.restaurant_id;
    if (!acc[restId]) {
      acc[restId] = {
        restaurant_name: manager.restaurant_name,
        managers: []
      };
    }
    acc[restId].managers.push(manager);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Store Managers</h2>
          <p className="text-muted-foreground">
            Manage store managers and their restaurant assignments
          </p>
        </div>
        <Button onClick={() => setAddDialog(true)} className="gap-2" data-testid="add-store-manager-btn">
          <Plus className="w-4 h-4" />
          Add Store Manager
        </Button>
      </div>

      {/* Store Managers Table */}
      <Card className="shadow-lg border-0 bg-white/90 backdrop-blur-xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserCircle className="w-5 h-5" />
            Store Manager List
          </CardTitle>
          <CardDescription>
            All store managers mapped to their respective restaurants
          </CardDescription>
        </CardHeader>
        <CardContent>
          {storeManagers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <UserCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No store managers added yet</p>
              <p className="text-sm">Click "Add Store Manager" to get started</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storeManagers.map((manager) => (
                  <TableRow key={manager.id} data-testid={`store-manager-row-${manager.id}`}>
                    <TableCell className="font-medium">{manager.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-gray-400" />
                        {manager.restaurant_name}
                      </div>
                    </TableCell>
                    <TableCell>{manager.phone || "-"}</TableCell>
                    <TableCell>{manager.email || "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(manager)}
                          data-testid={`edit-manager-${manager.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDialog({ isOpen: true, managerId: manager.id })}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-manager-${manager.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={addDialog || editDialog.isOpen} onOpenChange={(open) => {
        if (!open) {
          setAddDialog(false);
          setEditDialog({ isOpen: false, manager: null });
          resetForm();
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editDialog.isOpen ? "Edit Store Manager" : "Add Store Manager"}</DialogTitle>
            <DialogDescription>
              {editDialog.isOpen ? "Update store manager details" : "Add a new store manager and assign to a restaurant"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="Enter store manager name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="manager-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Restaurant *</Label>
              <Select
                value={formData.restaurant_id}
                onValueChange={(value) => setFormData({ ...formData, restaurant_id: value })}
              >
                <SelectTrigger data-testid="manager-restaurant-select">
                  <SelectValue placeholder="Select restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants && restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                placeholder="Enter phone number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="manager-phone-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="Enter email address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="manager-email-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddDialog(false);
              setEditDialog({ isOpen: false, manager: null });
              resetForm();
            }}>
              Cancel
            </Button>
            <Button 
              onClick={editDialog.isOpen ? handleUpdate : handleAdd} 
              disabled={loading}
              data-testid="save-manager-btn"
            >
              {loading ? "Saving..." : (editDialog.isOpen ? "Update" : "Add")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog({ isOpen: open, managerId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Store Manager</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this store manager? This action cannot be undone.
              Note: You cannot delete a store manager if they are assigned to any restaurant targets.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StoreManagerSettings;
